import axios from 'axios';
// Add missing import
import { jwtDecode } from 'jwt-decode';

// ✅ API base URL từ môi trường
const API_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Gửi cookie (dùng cho session Google OAuth)
});

// Helpers: token
const getAccessToken = () => localStorage.getItem('accessToken');
const getRefreshToken = () => localStorage.getItem('refreshToken');
const getOldToken = () => localStorage.getItem('token');

const saveTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
};

const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Làm mới access token nếu hết hạn
const refreshAccessToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');

    const response = await api.post('/auth/refresh-token', { refreshToken });

    if (response.data?.accessToken) {
      saveTokens(response.data.accessToken, null);
      return response.data.accessToken;
    } else {
      throw new Error('Invalid response from refresh token endpoint');
    }
  } catch (error) {
    // console.error('Error refreshing token:', error);
    clearTokens();
    throw error;
  }
};

// Request Interceptor – đính kèm token
api.interceptors.request.use(config => {
  // console.log('Sending request to:', config.url);
  
  const accessToken = getAccessToken();
  if (accessToken) {
    // console.log('Using access token for request');
    config.headers['Authorization'] = `Bearer ${accessToken}`;
    return config;
  }

  const oldToken = getOldToken();
  if (oldToken) {
    // console.log('Using legacy token for request');
    config.headers['x-auth-token'] = oldToken;
  }

  return config;
}, error => {
  // console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Response Interceptor – xử lý token hết hạn
api.interceptors.response.use(
  response => {
    // console.log('Response received from:', response.config.url, 'Status:', response.status);
    return response;
  },
  async error => {
    // console.error('Response error:', error);
    
    const originalRequest = error.config;
    if (!originalRequest) {
      // console.error('No original request found in error');
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const isTokenExpired =
        error.response?.data?.expired ||
        error.response?.data?.msg?.includes('Token') ||
        error.response?.data?.message?.includes('Token');

      if (isTokenExpired) {
        // console.log('Token expired, attempting to refresh...');
        originalRequest._retry = true;
        try {
          const newAccessToken = await refreshAccessToken();
          // console.log('Token refreshed successfully');
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          delete originalRequest.headers['x-auth-token'];
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

//
// 🔐 AUTH
//
export const register = (userData) => api.post('/auth/signup', userData);
export const confirmEmail = (token) => api.get(`/auth/confirm/${token}`);

export const login = async (userData) => {
  try {
    // console.log('Calling login API with:', userData.email);
    const response = await api.post('/auth/login', userData);
    // console.log('Login API response:', response.data);
    
    // Xử lý token mới (JWT)
    if (response.data.accessToken && response.data.refreshToken) {
      // console.log('Received JWT tokens');
      localStorage.removeItem('token'); // Xóa token cũ nếu có
      
      // Lưu tokens mới
      saveTokens(response.data.accessToken, response.data.refreshToken);
      
      // Lưu thông tin user
      try {
        const user = jwtDecode(response.data.accessToken);
        // console.log('Decoded user from JWT:', user);
        localStorage.setItem('user', JSON.stringify(user));
      } catch (jwtError) {
        // console.error('Error decoding JWT:', jwtError);
        throw new Error('Invalid JWT token received');
      }
    } 
    // Xử lý token cũ
    else if (response.data.token) {
      // console.log('Received legacy token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.setItem('token', response.data.token);
      
      // Lưu thông tin user
      try {
        const user = jwtDecode(response.data.token);
        // console.log('Decoded user from legacy token:', user);
        localStorage.setItem('user', JSON.stringify(user));
      } catch (jwtError) {
        // console.error('Error decoding legacy token:', jwtError);
        throw new Error('Invalid token received');
      }
    } else {
      // console.error('No token in response:', response.data);
      throw new Error('No token received from server');
    }
    
    // Verify tokens were stored
    // console.log('Tokens after login:', {
    //   accessToken: localStorage.getItem('accessToken'),
    //   refreshToken: localStorage.getItem('refreshToken'),
    //   legacyToken: localStorage.getItem('token'),
    //   user: localStorage.getItem('user')
    // });
    
    // Wait a bit to ensure localStorage is updated
    await new Promise(resolve => setTimeout(resolve, 300));
    return response;
  } catch (error) {
    console.error('Login function error:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken });
    }
  } catch (error) {
    console.error('Lỗi khi đăng xuất:', error);
  } finally {
    clearTokens();
  }
};

// 👉 Dùng sau khi Google login redirect về (OAuthSuccess)
export const loginWithGoogleSession = async () => {
    try {
      //console.log('Đang gọi API /auth/me để nhận thông tin người dùng và token');
      const response = await api.get('/auth/me');
      
      if (response.data?.success && response.data?.user) {
        const { user, accessToken, refreshToken } = response.data;
        
        
        // Lưu tokens vào localStorage
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
          //console.log('Đã lưu accessToken vào localStorage');
        }
        
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
          //console.log('Đã lưu refreshToken vào localStorage');
        }
        
        // Lưu thông tin người dùng
        localStorage.setItem('user', JSON.stringify(user));
        //console.log('Đã lưu thông tin user vào localStorage');
        
        // Tạo sự kiện auth state changed để thông báo cho app
        const authEvent = new Event('auth_state_changed');
        window.dispatchEvent(authEvent);
        //console.log('Đã phát sự kiện auth_state_changed');
        
        return response.data;
      } else {
        //console.error('Không nhận được thông tin user hoặc token:', response.data);
        throw new Error('Không lấy được thông tin người dùng hoặc token');
      }
    } catch (error) {
      //console.error('Lỗi khi xác thực phiên đăng nhập Google:', error);
      clearTokens();
      throw error;
    }
  };

export const logoutGoogle = async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    // console.error('Lỗi khi đăng xuất Google:', error);
  } finally {
    clearTokens();
    window.location.href = '/login';
  }
};

export const findPassword = (email) => api.post('/auth/findPassword', email);
export const resetPassword = (token, passwordData) => api.post(`/auth/resetPassword/${token}`, passwordData);
export const resendConfirmationEmail = (email) => api.post('/auth/resend-confirmation', { email });

//
// 👤 USER
//
export const getUserProfile = () => api.get('/user/profile');
export const updateUserProfile = (data) => api.put('/user/profile', {
  firstName: data.firstName,
  lastName: data.lastName,
  phone: data.phone,
});
export const updatePassword = (data) => api.post('/user/updatePassword', data);

//
// 📝 QUIZ
//
export const getQuizzes = () => api.get('/test');
export const getQuiz = (id) => api.get(`/test/${id}`);
export const submitQuiz = (id, answers) => api.post(`/test/${id}/submit`, { answers });

//
// 📊 RESULT
//
export const getUserResults = (userId) => api.get(`/result/user/${userId}`);
export const getAllQuestions = () => api.get('/test/questions/all');

//
// 💰 PAYMENT
//
export const checkQuizPurchase = async (quizId) => {
  const res = await api.get(`/payment/check-purchase?quizId=${quizId}`);
  return res.data;
};

export const createCheckoutSession = async (quizId) => {
  const res = await api.post('/payment/create-checkout-session', { quizId });
  return res.data;
};



export default api;